import {
  type Address,
  type ChainId,
  getChainAddresses,
  Market,
  type MarketParams
} from '@morpho-org/blue-sdk'
import { blueAbi } from '@morpho-org/blue-sdk-viem'
import { decodeFunctionResult, encodeFunctionData, size } from 'viem'

import { type SimulatedCall, toSimulationTx, type Transaction } from '@/services/simulate/simulate'

/**
 * The two read staticcalls appended to a market-actions-v2 simulated bundle for
 * "Option B": `Morpho.position(id, user)` then `Morpho.market(id)`. They observe
 * the **post-action** protocol state (after the action's `accrueInterest`) under
 * `eth_simulateV1`, which executes the bundle sequentially on one evolving state.
 *
 * They are appended **in this order**, so in the simulation result they are the
 * last two calls: position at `calls.at(-2)`, market at `calls.at(-1)`. The SDK
 * only ever *prepends* authorization txs, never appends, so this tail alignment
 * holds regardless of how many approvals the bundle carries.
 *
 * Both are `view` (no logs, no value moved) so they don't perturb the bundle's
 * `assetChanges`, and their `from` is the connected sender — `eth_simulateV1`
 * rejects a bundle whose txs don't share one `from`.
 */
export const POSITION_READ_CALL_COUNT = 2

/** Exact ABI-encoded byte sizes of the two reads' returnData: `position` returns 3 words
 *  (supplyShares, borrowShares, collateral), `market` returns 6. viem's decoder silently
 *  ignores EXTRA trailing bytes, so without these exact-size checks a misaligned tail (e.g.
 *  a market blob decoded as a position) would "decode" garbage instead of falling back. */
const POSITION_RETURN_DATA_SIZE = 3 * 32
const MARKET_RETURN_DATA_SIZE = 6 * 32

/**
 * Builds the `[position, market]` read txs for the bundle's tail. Returns
 * `undefined` on a chain blue-sdk doesn't know (`getChainAddresses` throws), so
 * the caller falls back to the JS-math estimate rather than simulating a bad bundle.
 *
 * @param params.chainId - The market's chain (resolves the Morpho Blue address).
 * @param params.marketParams - The target market (`.id` is the on-chain `bytes32` id).
 * @param params.user - The connected sender; must match the rest of the bundle's `from`.
 */
export const buildMarketPositionReadTxs = ({
  chainId,
  marketParams,
  user
}: {
  chainId: ChainId
  marketParams: MarketParams
  user: Address
}): Transaction[] | undefined => {
  let morpho: Address
  try {
    morpho = getChainAddresses(chainId).blue
  } catch {
    return undefined
  }

  const id = marketParams.id
  const positionData = encodeFunctionData({
    abi: blueAbi,
    functionName: 'position',
    args: [id, user]
  })
  const marketData = encodeFunctionData({ abi: blueAbi, functionName: 'market', args: [id] })

  return [
    toSimulationTx(user, { to: morpho, value: 0n, data: positionData }),
    toSimulationTx(user, { to: morpho, value: 0n, data: marketData })
  ]
}

/**
 * Decodes the post-action collateral + debt + supply from the bundle's trailing two
 * reads (the result of {@link buildMarketPositionReadTxs}). `borrowShares` is converted
 * to assets through a reconstructed blue-sdk `Market`, which rounds **up** — exactly the
 * protocol's `toAssetsUp`, so the displayed debt never undershoots the real one.
 * `supplyShares` rounds **down** (the protocol's `toAssetsDown` for supply), so the
 * displayed lend position never overshoots what a withdraw could redeem.
 *
 * Returns `undefined` (→ caller shows the JS-math estimate) when the reads are
 * absent or malformed: a short `calls` array (e.g. a backend that didn't relay
 * them), fewer calls than the bundle we posted (a relay that dropped entries —
 * the route only ever *prepends*, so `calls.length < expectedCallCount` means the
 * tail can't be trusted), a reverted read (`status === false`), a returnData whose
 * byte size isn't the exact ABI size of the expected tuple (viem ignores extra
 * trailing bytes, so a misaligned tail would otherwise decode garbage as verified
 * values), or a decode failure. LTV is intentionally **not** derived here — the
 * recap recomputes it from the resolved collateral/loan so the displayed value
 * uses one formula across initial and final.
 *
 * @param params.calls - The relayed per-call `returnData` (`calls.at(-2)`/`(-1)` are the reads).
 * @param params.expectedCallCount - Number of txs the caller posted to `/api/simulate` (reads included).
 * @param params.marketParams - The target market params (rebuilds the `Market` for the shares→assets math).
 */
export const decodeMarketPositionReads = ({
  calls,
  expectedCallCount,
  marketParams
}: {
  calls: readonly SimulatedCall[] | undefined
  expectedCallCount: number
  marketParams: MarketParams
}): { collateral: bigint; borrowAssets: bigint; supplyAssets: bigint } | undefined => {
  if (!calls || calls.length < POSITION_READ_CALL_COUNT) return undefined
  // The route relays one entry per simulated tx and only ever PREPENDS (authorizations), so
  // fewer entries than we posted means the relay dropped calls and the tail alignment is broken.
  if (calls.length < expectedCallCount) return undefined

  const positionCall = calls.at(-2)
  const marketCall = calls.at(-1)
  if (!positionCall || !marketCall) return undefined
  // `status` is always true on a successful bundle today (a revert throws the whole simulation),
  // but if the backend ever relays per-call reverts, a reverted read must not be decoded.
  if (positionCall.status === false || marketCall.status === false) return undefined
  // Exact-size checks — see POSITION_RETURN_DATA_SIZE: viem tolerates oversized returnData, so
  // these are what actually catch a misaligned tail rather than decoding it into garbage.
  if (
    size(positionCall.returnData) !== POSITION_RETURN_DATA_SIZE ||
    size(marketCall.returnData) !== MARKET_RETURN_DATA_SIZE
  )
    return undefined

  try {
    const [supplyShares, borrowShares, collateral] = decodeFunctionResult({
      abi: blueAbi,
      functionName: 'position',
      data: positionCall.returnData
    })
    const [
      totalSupplyAssets,
      totalSupplyShares,
      totalBorrowAssets,
      totalBorrowShares,
      lastUpdate,
      fee
    ] = decodeFunctionResult({
      abi: blueAbi,
      functionName: 'market',
      data: marketCall.returnData
    })

    const market = new Market({
      params: marketParams,
      totalSupplyAssets,
      totalSupplyShares,
      totalBorrowAssets,
      totalBorrowShares,
      lastUpdate,
      fee
    })

    return {
      collateral,
      // Rounds up by default (= the protocol's `toAssetsUp` for debt).
      borrowAssets: market.toBorrowAssets(borrowShares),
      // Rounds down by default (= the protocol's `toAssetsDown` for supply).
      supplyAssets: market.toSupplyAssets(supplyShares)
    }
  } catch {
    return undefined
  }
}
